import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import Payment, { FormType } from '@solidgate/react-sdk';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { Loader } from '../Loader';
import { paymentErrorMapper } from '../../../lib/mappers/payment-error-mapper';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import visaImageSrc from '../../assets/images/subscription/visa.png';
import mastercardImageSrc from '../../assets/images/subscription/mastercard.png';
import styles from './PaymentForm.css';

const cx = classnames.bind(styles);

function getInitialFormParams() {
    return {
        allowSubmit: false,
        isCardHolderVisible: false,
        formTypeClass: FormType.Default,
        googleFontLink: '//fonts.googleapis.com/css2?family=Inter:wght@400&display=swap',
        cardNumberPlaceholder: 'xxxx xxxx xxxx xxxx',
        cardNumberLabel: 'Card Number',
        cardExpiryDatePlaceholder: 'MM / YY',
        cardExpiryDateLabel: 'Expiry Date',
        cardCvvPlaceholder: 'CVC',
        cardCvvLabel: 'Security Code',
        cardBrands: ['visa', 'mastercard']
    };
}

const paypalButtonParams = {
    enabled: true,
    color: 'gold',
    label: 'buynow',
    shape: 'rect',
    height: 52
};

const customFormStyles = {
    'form_body': {
        'font-family': 'Inter, sans-serif',
        'width': '100%',
        '.two-columns': {
            'grid-column-gap': '16px',
            'grid-template-columns': 'repeat(2, 1fr)',
            'margin': '8px 0'
        },
        '.form-container': {
            'padding-top': '0px'
        }
    },
    'card_brands': {
        'justify-content': 'center',
        'padding': '0',
        'gap': '8px',
        'i': {
            'width': '21px',
            'height': '16px'
        }
    },
    'input_group': {
        '.label': {
            'color': '#0F172A',
            'font-size': '14px',
            'line-height': '20px',
            'font-weight': '600',
            'margin-bottom': '4px'
        },
        '.input-block input': {
            'padding-left': '16px !important',
            'padding': '18px 14px',
            'height': 'auto',
            'font-size': '16px',
            'line-height': '24px',
            'font-weight': '600',
            'color': '#0F172A',
            'letter-spacing': '0',
            'border-radius': '8px',
            'background-color': '#FFFFFF',
            'border-color': '#4D609A',
            'border-width': '2px',
            '::placeholder': {
                'font-size': '16px',
                'font-family': 'Inter, sans-serif',
                'line-height': '24px !important',
                'font-weight': '400',
                'color': '#545F92',
                'letter-spacing': '0px',
                'position': 'static'
            },
            ':focus': {
                'border-color': '#1C59FF'
            }
        },
        '.input-block.error input': {
            'border-color': '#F70000',
            'box-shadow': 'none'
        },
        '.input-block.error .label': {
            'color': '#0F172A'
        },
        '.input-block.not-empty .label': {
            'font-size': '14px',
            'color': '#0F172A'
        },
        '.error-text': {
            'font-size': '12px',
            'font-weight': '400',
            'line-height': '16px',
            'margin-top': '4px',
            'padding': '0',
            'color': '#F70000',
            '.triangle': {
                'display': 'none'
            }
        },
        '.brand-icon': {
            'bottom': '19px',
            'right': '16px',
            'left': 'unset',
            'top': 'unset'
        },
        '.tooltip-icon': {
            'display': 'none'
        }
    }
};

const VISA_BRAND = 'VISA';
const MASTERCARD_BRAND = 'MASTERCARD';
const AMEX_BRAND = 'AMERICAN EXPRESS';
const CARD_BRANDS = [VISA_BRAND, MASTERCARD_BRAND, AMEX_BRAND];

const FAST_PAYMENT_ITEM = 'fastPayment';
const CARD_ITEM = 'card';

function PaymentForm({ className, merchantData, setErrorData, setIsSuccessful }) {
    const initialFormParams = getInitialFormParams();
    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });
    const [isMounted, setIsMounted] = useState(false);
    const [form, setForm] = useState(null);
    const paypalContainerRef = useRef();
    const [activeItem, setActiveItem] = useState(CARD_ITEM);

    const changeActiveItem = item => () => {
        setActiveItem(item);
    };

    const handleOnReadyPaymentInstance = useCallback(newForm => {
        setForm(newForm);
    }, []);

    const handleMounted = useCallback((e) => {
        if (e.entity === 'form') {
            setIsMounted(true);
        }
    }, []);

    const [isPending, setIsPending] = useState(false);
    const [card, setCard] = useState({});
    const [errorText, setErrorText] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();

        logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'card' });
        logEvent(EVENT_TYPES.SALE_CHECKOUT_CLICK_PAY);

        if (card?.brand && (!CARD_BRANDS.includes(card.brand))) {
            return setErrorText('Please use Visa, MasterCard or American Express select a different payment method.');
        }

        if (form) {
            form.submit();
        } else {
            console.error('Solidgate Payment ref is not available.');
        }
    };

    const showErrorModal = errorCode => {
        setErrorData({ ...paymentErrorMapper.toDomain(errorCode), isModalVisible: true });
    };

    const handleOnError = (e) => {
        console.log(e, 'error');

        setIsPending(true);
        showErrorModal(e.code);
    };

    const handleOnFail = e => {
        console.log(e, 'fail');

        setIsPending(true);
        showErrorModal(e.code);
    };

    const handleOrderStatus = (e) => {
        console.log(e, 'order status');

        if (e.response.order?.status !== '3ds_verify') {
            setIsPending(true);
        }

        if (e.response.error?.code) {
            return showErrorModal(e.response.error.code);
        }

        if (e.response.order?.status === 'auth_ok' || e.response.order.status === 'settle_ok') {
            setIsSuccessful(true);
        }
    };

    const handleOnSuccess = () => {
        setIsPending(true);
        setIsSuccessful(true);
    };

    const handleOnInteraction = (e) => {
        if (e.target?.type === 'input' && e.target?.interaction === 'change' && errorText) {
            setErrorText('');
        }

        if (e.target?.type === 'button' && e.target?.interaction === 'click') {
            if (e.target.name === 'paypal') {
                logEvent(EVENT_TYPES.USER_PROPERTIES, { 'payment_provider': 'paypal' });
            }

            logEvent(EVENT_TYPES.SALE_CHECKOUT_CLICK_PAY);
        }
    };

    const handleCard = cardData => {
        if (cardData?.card) {
            setCard(cardData.card);
        }
    };

    return (
        <form className={classes} onSubmit={handleSubmit}>
            {/* <div className={cx('accordionItem', 'accordionItemFirst')}>
                <button
                    type='button'
                    onClick={changeActiveItem(FAST_PAYMENT_ITEM)}
                    className={styles.accordionItemHeader}
                >
                    <div className={cx('checkbox', { active: activeItem === FAST_PAYMENT_ITEM })}>
                        <span className={styles.checkmark} />
                    </div>
                    <span>Fast Payment</span>
                </button>
                <div className={cx('accordionItemContent', { visible: activeItem === FAST_PAYMENT_ITEM })}>
                    <p className={cx('informationText', 'informationTextOnMobile')}>The safer, easier way to pay</p>
                    <div className={cx('paymentButtons', { mounted: isMounted })}>
                        <div ref={paypalContainerRef} />
                    </div>
                </div>
            </div>
            {isMounted && (
                <>
                    <p className={cx('informationText', 'informationTextOnLarge')}>The safer, easier way to pay</p>
                    <p className={styles.paymentDivider}>
                        <span>or pay with a card</span>
                    </p>
                </>
            )} */}
            <div className={styles.accordionItem}>
                <button
                    type='button'
                    onClick={changeActiveItem(CARD_ITEM)}
                    className={styles.accordionItemHeader}
                >
                    <div className={cx('checkbox', { active: activeItem === CARD_ITEM })}>
                        <span className={styles.checkmark} />
                    </div>
                    <span>Credit card</span>
                    {activeItem !== CARD_ITEM && (
                        <ul className={styles.paymentMethods}>
                            <li><img src={visaImageSrc} alt='visa' /></li>
                            <li><img src={mastercardImageSrc} alt='mastercard' /></li>
                        </ul>
                    )}
                </button>
                <div className={cx('accordionItemContent', { visible: activeItem === CARD_ITEM })}>
                    {isPending
                        ? <div className={styles.loaderWrapper}><Loader variant='secondary' className={styles.loader} /></div>
                        : (
                            <Payment
                                googlePayButtonParams={{ enabled: false }}
                                // paypalButtonParams={paypalButtonParams}
                                // paypalContainerRef={paypalContainerRef}
                                merchantData={merchantData}
                                styles={customFormStyles}
                                formParams={initialFormParams}
                                onError={handleOnError}
                                onFail={handleOnFail}
                                onCard={handleCard}
                                onOrderStatus={handleOrderStatus}
                                onSuccess={handleOnSuccess}
                                onInteraction={handleOnInteraction}
                                onMounted={handleMounted}
                                onReadyPaymentInstance={handleOnReadyPaymentInstance}
                            />
                        )
                    }
                    {isMounted && (
                        <>
                            <Button
                                label='Subscribe'
                                onClick={handleSubmit}
                                className={styles.button}
                                isLoading={isPending}
                            />
                            {Boolean(errorText) && <p className={styles.errorText}>{errorText}</p>}
                        </>
                    )}
                </div>
            </div>
        </form>
    );
}

PaymentForm.propTypes = {
    className: PropTypes.string,
    setIsSuccessful: PropTypes.func.isRequired,
    setErrorData: PropTypes.func.isRequired,
    merchantData: PropTypes.object.isRequired
};

PaymentForm.defaultProps = {
    className: ''
};

export {
    PaymentForm
};
